<template>
  <div class="accommodation-container">
    <v-card class="date-selection-card mb-4 elevation-2">
      <v-card-title class="date-selection-header">
        <h3 class="earthwerk-font">Select Your Dates</h3>
      </v-card-title>
      <v-card-text>
        <v-alert
          v-if="hasGlobalBookingDates"
          type="info"
          class="mb-3"
          icon="mdi-calendar-check"
          dense
          dismissible>
          <strong>Your selected stay dates:</strong> {{ formattedGlobalCheckIn }} to {{ formattedGlobalCheckOut }}
          <span class="ml-2 grey--text text-caption">({{ globalBookingDays }} days)</span>
          <br>
          <small class="text-caption">You can modify dates below if needed (must be within your stay period).</small>
        </v-alert>

        <v-alert
          v-if="hasGlobalGuests"
          type="success"
          class="mb-3"
          icon="mdi-account-group"
          dense
          dismissible>
          <strong>Your selected guests:</strong> {{ formattedGlobalGuests }}
          <br>
          <small class="text-caption">Guest numbers will be pre-filled when adding accommodations.</small>
        </v-alert>

        <v-layout row wrap>
          <v-flex md6 sm12 class="pa-2">
            <v-dialog
              ref="startDateDialog"
              persistent
              v-model="modalStartDate"
              lazy
              full-width
              width="290px"
              :return-value.sync="facility.start_date">
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-on="on"
                  :label="hasGlobalBookingDates ? 'Start Date (Optional)' : 'Start Date *'"
                  v-model="facility.start_date"
                  readonly
                  prepend-icon="event"
                  :hint="hasGlobalBookingDates ? `Must be between ${formattedGlobalCheckIn} and ${formattedGlobalCheckOut}` : ''"
                  persistent-hint
                  class="date-input"></v-text-field>
              </template>
              <v-date-picker
                v-model="facility.start_date"
                scrollable
                :min="minStartDate"
                :max="maxStartDate"
                @input="resetForAvailability()">
                <v-spacer></v-spacer>
                <v-btn flat color="primary" @click="modalStartDate = false">Cancel</v-btn>
                <v-btn flat color="primary" @click="$refs.startDateDialog.save(facility.start_date)">OK</v-btn>
              </v-date-picker>
            </v-dialog>
          </v-flex>

          <v-flex md6 sm12 class="pa-2">
            <v-dialog
              ref="endDateDialog"
              persistent
              v-model="modalEndDate"
              lazy
              full-width
              width="290px"
              :return-value.sync="facility.end_date">
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-on="on"
                  :label="hasGlobalBookingDates ? 'End Date (Optional)' : 'End Date *'"
                  v-model="facility.end_date"
                  readonly
                  :disabled="!facility.start_date"
                  prepend-icon="event"
                  :hint="hasGlobalBookingDates ? `Must be after start date and within ${formattedGlobalCheckOut}` : ''"
                  persistent-hint
                  class="date-input"></v-text-field>
              </template>
              <v-date-picker
                v-model="facility.end_date"
                scrollable
                :min="minEndDate"
                :max="maxEndDate"
                @input="searchFacilityAvailability()">
                <v-spacer></v-spacer>
                <v-btn flat color="primary" @click="modalEndDate = false">Cancel</v-btn>
                <v-btn flat color="primary" @click="$refs.endDateDialog.save(facility.end_date)">OK</v-btn>
              </v-date-picker>
            </v-dialog>
          </v-flex>
        </v-layout>

        <v-flex v-if="facility.start_date && facility.end_date && allAvailabileFacilities && allAvailabileFacilities.length > 0">
          <h4 class="earth-color">AVAILABLE ACCOMMODATIONS: {{ allAvailabileFacilities.length }}</h4>
          <small class="slots-explanation">
            Showing facilities available from {{ facility.start_date }} to {{ facility.end_date }}
          </small>
        </v-flex>
      </v-card-text>
    </v-card>

    <div v-if="showFullPageLoader" class="full-page-loader-overlay">
      <div class="loader-content">
        <v-progress-circular indeterminate color="#74a533" size="64"></v-progress-circular>
        <p class="loader-text mt-3">Finding accommodations...</p>
      </div>
    </div>

    <div class="accommodations-grid-container" :class="{ 'loading-active': showFullPageLoader }">
      <v-layout row wrap v-if="!showFullPageLoader && allAvailabileFacilities && allAvailabileFacilities.length > 0">
        <v-flex
          align-content-space-between
          md4
          sm6
          xs12
          v-for="accommodation in allAvailabileFacilities"
          :key="accommodation.public_id">
          <v-container fluid class="pa-2">
            <v-card flat hover class="accommodation-card elevation-2">
              <v-img
                class="accommodation-image"
                :src="accommodation.thumbnail"
                aspect-ratio="1.5"
                gradient="to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.3)">
                <v-container fill-height class="pa-0">
                  <v-layout align-end class="image-overlay">
                    <v-flex xs12>
                      <span class="accommodation-badge" v-if="accommodation.pricing_status === 'true'">
                        <template v-if="accommodation.accomodation_type_id === 'Stables'">
                          {{ $localCurrency }} {{ $roundOff(convert1(accommodation.price[0].currency_type, accommodation.price[0].mandatory_price).amount) }}
                        </template>
                        <template v-else>
                          From {{ $localCurrency }} {{ $roundOff(convert1(accommodation.price[0].currency_type, accommodation.price[0].price).amount) }}
                        </template>
                      </span>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-img>

              <v-container fluid class="pt-3 pb-0">
                <h3 class="accommodation-title earthwerk-font">{{ accommodation.name.toUpperCase() }}</h3>

                <div class="capacity-display">
                  <div class="capacity-item">
                    <v-icon small color="grey">people</v-icon>
                    <span class="capacity-label">Maximum Capacity:</span>
                    <span class="capacity-value">{{ accommodation.maximum_guests }} Guests</span>
                  </div>
                  <div class="capacity-item" v-if="accommodation.quantity">
                    <v-icon small color="grey">hotel</v-icon>
                    <span class="capacity-label">Available Units:</span>
                    <span class="capacity-value">{{ accommodation.quantity }}</span>
                  </div>
                </div>

                <v-icon small @click="openInfo(accommodation)" class="info-icon">info_outline</v-icon>

                <div class="accommodation-details mt-2">
                  <span v-if="accommodation.pricing_status === 'true'">
                    <div v-if="accommodation.accomodation_type_id === 'Stables'" class="price-info">
                      <div class="price-pack">
                        <span>Base Price: </span>
                        <span>{{ $localCurrency }} {{ $roundOff(convert1(accommodation.price[0].currency_type, accommodation.price[0].mandatory_price).amount) }} per night</span>
                      </div>
                    </div>
                    <div v-else class="price-info">
                      <div v-for="(price, key) in developPricePacks(accommodation)" :key="key" class="price-pack">
                        <span>{{ price.lower_limit }}-{{ price.upper_limit }} guests: </span>
                        <span>{{ $localCurrency }} {{ $roundOff(convert1(price.currency, price.price).amount) }} per night</span>
                      </div>
                    </div>
                  </span>
                </div>

                <v-btn
                  color="primary"
                  large
                  dark
                  class="book-now-btn earthwerk-font mt-3"
                  :disabled="accommodation.pricing_status !== 'true'"
                  @click="openBookingDialog(accommodation)">
                  Add to Booking
                </v-btn>
              </v-container>
            </v-card>
          </v-container>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-else-if="!showFullPageLoader && (!facility.start_date || !facility.end_date)">
        <v-flex xs12>
          <v-card class="text-center pa-5">
            <v-icon size="64" color="grey lighten-2">event</v-icon>
            <h4 class="grey--text">Please select start and end dates to see available accommodations</h4>
          </v-card>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-else-if="!showFullPageLoader && facility.start_date && facility.end_date && allAvailabileFacilities.length === 0">
        <v-flex xs12>
          <v-card class="text-center pa-5">
            <v-icon size="64" color="grey lighten-2">hotel</v-icon>
            <h4 class="grey--text">No accommodations available for the selected dates</h4>
            <p class="grey--text">Please try different dates</p>
            <v-btn color="primary" class="earthwerk-font mt-3" @click="resetForAvailability">
              Change Dates
            </v-btn>
          </v-card>
        </v-flex>
      </v-layout>
    </div>

    <v-dialog v-model="infoDialog" persistent max-width="900px">
      <v-card class="info-dialog">
        <div class="gallery-section">
          <div v-if="infoImages && infoImages.length > 0" class="gallery-container">
            <div class="image-navigation">
              <button class="nav-button prev" @click="prevInfoImage" :disabled="currentInfoImageIndex === 0">
                <v-icon>chevron_left</v-icon>
              </button>
              <div class="image-wrapper">
                <img :src="infoImages[currentInfoImageIndex].path" class="gallery-image" alt="Facility image">
              </div>
              <button class="nav-button next" @click="nextInfoImage" :disabled="currentInfoImageIndex === infoImages.length - 1">
                <v-icon>chevron_right</v-icon>
              </button>
            </div>
            <div class="image-counter">
              {{ currentInfoImageIndex + 1 }} / {{ infoImages.length }}
            </div>
          </div>
          <div v-else class="single-image-container">
            <img :src="currentInfoFacility ? currentInfoFacility.thumbnail : ''" class="gallery-image" alt="Facility thumbnail">
          </div>
        </div>

        <v-card-text class="details-section">
          <div class="details-header">
            <h1 class="facility-title earthwerk-font">{{ currentInfoFacility ? currentInfoFacility.name.toUpperCase() : '' }}</h1>
            <div class="capacity-display">
              <div class="capacity-item">
                <span class="capacity-label">Maximum Capacity:</span>
                <span class="capacity-value">{{ currentInfoFacility ? currentInfoFacility.maximum_guests : '' }} Guests</span>
              </div>
            </div>
          </div>
          <div class="description-section" v-html="currentInfoFacility ? currentInfoFacility.description : ''"></div>
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn flat @click="infoDialog = false" class="earthwerk-font">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" persistent max-width="800px" @keydown.esc="closeBookingDialog">
      <v-card class="booking-dialog">
        <v-card-title class="dialog-header">
          <h2 class="earthwerk-font">ADD TO BOOKING</h2>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeBookingDialog">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-tabs color="primary" dark slider-color="primary">
            <v-tab key="availability" class="earthwerk-font">Accommodation Details</v-tab>
            <v-tab-item key="availability">
              <div class="tab-content">
                <div class="date-pickers">
                  <v-text-field
                    label="Start Date"
                    v-model="bookingDetails.start_date"
                    readonly
                    prepend-icon="event"
                    class="date-input"
                    disabled></v-text-field>

                  <v-text-field
                    label="End Date"
                    v-model="bookingDetails.end_date"
                    readonly
                    prepend-icon="event"
                    class="date-input"
                    disabled></v-text-field>
                </div>

                <v-flex v-if="bookingDetails.start_date && bookingDetails.end_date && selectedFacility">
                  <h4 class="earth-color">AVAILABLE SLOTS : {{ availableSlots }}</h4>
                  <small class="slots-explanation">
                    (Maximum Capacity: {{ selectedFacility.maximum_guests }} × Available Units: {{ selectedFacility.quantity || 1 }})
                  </small>
                </v-flex>

                <div class="participant-selectors" v-if="showDates && bookingDetails.start_date && bookingDetails.end_date">
                  <div class="selector-group">
                    <v-autocomplete
                      label="Children"
                      :items="childSlots"
                      v-model="bookingDetails.children"
                      prepend-icon="child_friendly"
                      class="participant-input"
                      @change="calculateExtraPax"></v-autocomplete>

                    <v-autocomplete
                      label="Adults"
                      :items="adultSlots"
                      v-model="bookingDetails.adults"
                      prepend-icon="person"
                      class="participant-input"
                      @change="calculateExtraPax"></v-autocomplete>
                  </div>
                </div>

                <div v-if="hasGlobalGuests && (bookingDetails.adults > 0 || bookingDetails.children > 0)" class="global-guests-info">
                  <v-alert type="info" dense class="mb-2">
                    <v-icon small left>mdi-account-multiple</v-icon>
                    Booking for: {{ bookingDetails.adults }} Adult{{ bookingDetails.adults !== 1 ? 's' : '' }} and
                    {{ bookingDetails.children }} Child{{ bookingDetails.children !== 1 ? 'ren' : '' }}
                  </v-alert>
                </div>

                <div v-if="showExtraPaxSection" class="extra-pax-section">
                  <v-divider class="my-3"></v-divider>
                  <h4 class="earth-color">Extra Participants (Exceeds Standard Capacity)</h4>
                  <small class="slots-explanation">Additional charges apply for extra guests</small>

                  <div class="additional-options mt-3">
                    <v-text-field
                      label="Extra Adults"
                      type="number"
                      min="0"
                      v-model="bookingDetails.extra_pax_adults"
                      prepend-icon="person_add"
                      class="option-input"
                      @change="validateExtraPax"></v-text-field>

                    <v-text-field
                      label="Extra Children"
                      type="number"
                      min="0"
                      v-model="bookingDetails.extra_pax_children"
                      prepend-icon="child_care"
                      class="option-input"
                      @change="validateExtraPax"></v-text-field>
                  </div>
                </div>

                <div class="additional-options" v-if="selectedFacility && selectedFacility.accomodation_type_id === 'Stables' && (selectedFacility.name === 'The Stables Research Centre - Double Room (One Bed)' || selectedFacility.name === 'The Stables Research Centre - Twin Room (Two Beds)')">
                  <v-divider class="my-3"></v-divider>
                  <v-select
                    label="Number of Rooms"
                    :items="roomSlots"
                    v-model="bookingDetails.number_of_rooms"
                    prepend-icon="hotel"
                    class="option-input"></v-select>
                </div>

                <div class="additional-options" v-if="selectedFacility && selectedFacility.accomodation_type_id === 'Pelican'">
                  <v-divider class="my-3"></v-divider>
                  <v-select
                    label="Catering Type"
                    :items="cateringType"
                    item-text="facility_pricing_name"
                    item-value="facility_pricing_type_public_id"
                    v-model="bookingDetails.catering_type"
                    prepend-icon="restaurant"
                    class="option-input"></v-select>
                </div>

                <div class="price-summary" v-if="(bookingDetails.adults > 0 || bookingDetails.children > 0) && bookingDetails.start_date && bookingDetails.end_date">
                  <h3 class="earthwerk-font">Price Summary</h3>
                  <div class="price-breakdown">
                    <div v-if="selectedFacility && selectedFacility.accomodation_type_id === 'Stables'">
                      <div v-if="selectedFacility.maximum_guests == 1" class="price-line">
                        <span>Room Rate ({{ getDays() }} {{ getDays() === 1 ? 'night' : 'nights' }})</span>
                        <span>{{ $localCurrency }} {{ $roundOff(calculateTotalPrice()) }}</span>
                      </div>
                      <div v-else-if="selectedFacility.maximum_guests == 2" class="price-line">
                        <span>Room Rate ({{ getDays() }} {{ getDays() === 1 ? 'night' : 'nights' }})</span>
                        <span>{{ $localCurrency }} {{ $roundOff(calculateTotalPrice()) }}</span>
                      </div>
                      <div v-else>
                        <div class="price-line" v-if="bookingDetails.adults > 0">
                          <span>Adults ({{ bookingDetails.adults }} × {{ $localCurrency }} {{ $roundOff(getAdultPrice()) }} × {{ getDays() }} {{ getDays() === 1 ? 'night' : 'nights' }})</span>
                          <span>{{ $localCurrency }} {{ $roundOff(calculateAdultTotal()) }}</span>
                        </div>
                        <div class="price-line" v-if="bookingDetails.children > 0">
                          <span>Children ({{ bookingDetails.children }} × {{ $localCurrency }} {{ $roundOff(getChildPrice()) }} × {{ getDays() }} {{ getDays() === 1 ? 'night' : 'nights' }})</span>
                          <span>{{ $localCurrency }} {{ $roundOff(calculateChildTotal()) }}</span>
                        </div>
                      </div>
                    </div>

                    <div v-else-if="selectedFacility && selectedFacility.accomodation_type_id === 'Pelican' && bookingDetails.catering_type">
                      <div class="price-line">
                        <span>Package Rate ({{ getDays() }} {{ getDays() === 1 ? 'night' : 'nights' }})</span>
                        <span>{{ $localCurrency }} {{ $roundOff(calculateTotalPrice()) }}</span>
                      </div>
                    </div>

                    <div v-else-if="selectedFacility">
                      <div class="price-line" v-if="bookingDetails.adults > 0">
                        <span>Adults ({{ bookingDetails.adults }} × {{ $localCurrency }} {{ $roundOff(getAdultPrice()) }} × {{ getDays() }} {{ getDays() === 1 ? 'night' : 'nights' }})</span>
                        <span>{{ $localCurrency }} {{ $roundOff(calculateAdultTotal()) }}</span>
                      </div>
                      <div class="price-line" v-if="bookingDetails.children > 0">
                        <span>Children ({{ bookingDetails.children }} × {{ $localCurrency }} {{ $roundOff(getChildPrice()) }} × {{ getDays() }} {{ getDays() === 1 ? 'night' : 'nights' }})</span>
                        <span>{{ $localCurrency }} {{ $roundOff(calculateChildTotal()) }}</span>
                      </div>
                    </div>

                    <div v-if="bookingDetails.extra_pax_adults > 0" class="price-line extra-charge">
                      <span>Extra Adults (exceeds capacity) ({{ bookingDetails.extra_pax_adults }} × {{ $localCurrency }} {{ $roundOff(getExtraAdultRate()) }} × {{ getDays() }} nights)</span>
                      <span>{{ $localCurrency }} {{ $roundOff(calculateExtraAdultTotal()) }}</span>
                    </div>
                    <div v-if="bookingDetails.extra_pax_children > 0" class="price-line extra-charge">
                      <span>Extra Children (exceeds capacity) ({{ bookingDetails.extra_pax_children }} × {{ $localCurrency }} {{ $roundOff(getExtraChildRate()) }} × {{ getDays() }} nights)</span>
                      <span>{{ $localCurrency }} {{ $roundOff(calculateExtraChildTotal()) }}</span>
                    </div>

                    <div class="price-total" v-if="calculateTotalPriceWithExtras() > 0">
                      <strong>Total Amount:</strong>
                      <strong>{{ $localCurrency }} {{ $roundOff(calculateTotalPriceWithExtras()) }}</strong>
                    </div>
                    <div class="price-note" v-if="showExtraPaxSection">
                      <small class="grey--text">* Extra guest charges apply for guests exceeding standard capacity</small>
                    </div>
                  </div>
                </div>
              </div>
            </v-tab-item>
          </v-tabs>
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn flat @click="closeBookingDialog" class="cancel-btn earthwerk-font">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="addAccommodation()"
            class="reserve-btn earthwerk-font">
            Add to Booking
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'Accommodations',
  data() {
    return {
      dialog: false,
      infoDialog: false,
      currentInfoFacility: null,
      currentInfoImageIndex: 0,
      selectedFacility: null,
      modalStartDate: false,
      modalEndDate: false,
      today: this.$moment().format('YYYY-MM-DD'),
      showDates: false,
      showExtraPaxSection: false,
      showFullPageLoader: false,
      facility: {
        start_date: '',
        end_date: ''
      },
      bookingDetails: {
        start_date: '',
        end_date: '',
        children: 0,
        adults: 0,
        catering_type: '',
        accommodation_discount: 0,
        extra_pax_adults: 0,
        extra_pax_children: 0,
        number_of_rooms: 0
      },
      pricingWatcher: null,
      lastSearchedRange: null
    }
  },

  created() {
    if (this.$store.dispatch) {
      if (this.$store._actions && this.$store._actions['get_active_camping']) {
        this.$store.dispatch('get_active_camping').catch(function () {})
      }
      if (this.$store._actions && this.$store._actions['get_active_accommodation']) {
        this.$store.dispatch('get_active_accommodation').catch(function () {})
      }
      if (this.$store._actions && this.$store._actions['get_all_facility_pricing_types']) {
        this.$store.dispatch('get_all_facility_pricing_types').catch(function () {})
      }
    }
    this.loadDatesFromGlobalStore()
    this.loadGuestsFromStorage()
    this.$root.$on('booking-dates-changed', this.onBookingDatesChanged)
  },

  mounted() {
    this.$nextTick(() => {
      this.autoSearchIfDatesAvailable()
    })
  },

  beforeDestroy() {
    if (this.pricingWatcher) {
      this.pricingWatcher()
    }
    this.$root.$off('booking-dates-changed', this.onBookingDatesChanged)
  },

  computed: {
    globalBookingDates() {
      if (this.$store.state.GlobalBookingStore && this.$store.state.GlobalBookingStore.booking_dates) {
        return this.$store.state.GlobalBookingStore.booking_dates
      }
      return null
    },

    hasGlobalBookingDates() {
      return this.globalBookingDates &&
        this.globalBookingDates.check_in &&
        this.globalBookingDates.check_in !== '' &&
        this.globalBookingDates.check_out &&
        this.globalBookingDates.check_out !== ''
    },

    formattedGlobalCheckIn() {
      if (!this.hasGlobalBookingDates) return ''
      return this.$moment(this.globalBookingDates.check_in).format('MMM DD, YYYY')
    },

    formattedGlobalCheckOut() {
      if (!this.hasGlobalBookingDates) return ''
      return this.$moment(this.globalBookingDates.check_out).format('MMM DD, YYYY')
    },

    globalBookingDays() {
      return this.globalBookingDates ? this.globalBookingDates.days : 0
    },

    globalGuests() {
      if (this.$store.state.GlobalBookingStore && this.$store.state.GlobalBookingStore.booking_guests) {
        return this.$store.state.GlobalBookingStore.booking_guests
      }
      return { adults: 0, children: 0, total: 0 }
    },

    hasGlobalGuests() {
      return this.globalGuests.total > 0
    },

    formattedGlobalGuests() {
      if (!this.hasGlobalGuests) return ''
      return this.globalGuests.adults + ' Adult' + (this.globalGuests.adults !== 1 ? 's' : '') + ' and ' + this.globalGuests.children + ' Child' + (this.globalGuests.children !== 1 ? 'ren' : '')
    },

    standardCapacity() {
      if (!this.selectedFacility) return 0
      var maxGuests = Number(this.selectedFacility.maximum_guests || 0)
      var units = Number(this.selectedFacility.quantity || 1)
      return maxGuests * units
    },

    totalStandardGuests() {
      return Number(this.bookingDetails.adults || 0) + Number(this.bookingDetails.children || 0)
    },

    minStartDate() {
      if (this.hasGlobalBookingDates) {
        return this.globalBookingDates.check_in
      }
      return this.today
    },

    maxStartDate() {
      if (this.hasGlobalBookingDates && this.globalBookingDates.check_out) {
        return this.$moment(this.globalBookingDates.check_out).subtract(1, 'days').format('YYYY-MM-DD')
      }
      return null
    },

    minEndDate() {
      if (this.facility.start_date) {
        return this.$moment(this.facility.start_date).add(1, 'days').format('YYYY-MM-DD')
      }
      if (this.hasGlobalBookingDates) {
        return this.$moment(this.globalBookingDates.check_in).add(1, 'days').format('YYYY-MM-DD')
      }
      return this.today
    },

    maxEndDate() {
      if (this.hasGlobalBookingDates) {
        return this.globalBookingDates.check_out
      }
      return null
    },

    allAvailabileFacilities() {
      var facilities = []

      if (this.$store.state.BookingStore && this.$store.state.BookingStore.available_facilities) {
        facilities = this.$store.state.BookingStore.available_facilities
      } else if (this.$store.state.FacilityStore && this.$store.state.FacilityStore.available_facilities) {
        facilities = this.$store.state.FacilityStore.available_facilities
      } else if (this.$store.state.available_facilities) {
        facilities = this.$store.state.available_facilities
      }

      if (!Array.isArray(facilities)) {
        return []
      }

      return facilities.filter(function (facility) {
        var isNotCamping = facility.facility_type_id !== 'Camping Sites' &&
          facility.facility_type !== 'Camping Sites' &&
          !(facility.name && facility.name.toLowerCase().indexOf('campsite') !== -1)

        var isAccommodation = facility.accomodation_type_id === 'Stables' ||
          facility.accomodation_type_id === 'Pelican' ||
          facility.accomodation_type_id === 'Tented Camp' ||
          (facility.facility_type_id !== 'Camping Sites')

        var hasPricing = facility.pricing_status === 'true'

        return isNotCamping && isAccommodation && hasPricing
      })
    },

    pricedFacility() {
      if (this.$store.state.FacilityStore) {
        return this.$store.state.FacilityStore.facility_with_pricing || this.selectedFacility
      }
      return this.selectedFacility
    },

    infoImages() {
      if (this.$store.state.FacilityStore) {
        return this.$store.state.FacilityStore.facility_images || []
      }
      return []
    },

    cateringType() {
      if (this.$allPricingGroups && Array.isArray(this.$allPricingGroups)) {
        return this.$allPricingGroups
      }
      return []
    },

    availableSlots() {
      if (!this.selectedFacility) return 0
      var maxGuests = Number(this.selectedFacility.maximum_guests || 0)
      var quantity = Number(this.selectedFacility.quantity || 1)
      return maxGuests * quantity
    },

    childSlots() {
      var max = this.calculateChildMax
      var slots = []
      for (var i = 0; i <= max; i++) {
        slots.push(i)
      }
      return slots
    },

    adultSlots() {
      var max = this.calculateAdultMax
      var slots = []
      for (var i = 0; i <= max; i++) {
        slots.push(i)
      }
      return slots
    },

    roomSlots() {
      var available = this.selectedFacility ? Number(this.selectedFacility.quantity || 1) : 1
      var slots = []
      for (var i = 0; i <= available; i++) {
        slots.push(i)
      }
      return slots
    },

    calculateAdultMax() {
      if (!this.selectedFacility) return 10
      var maxGuests = Number(this.selectedFacility.maximum_guests || 0)
      var quantity = Number(this.selectedFacility.quantity || 1)
      var totalCapacity = maxGuests * quantity
      var totalGuests = Number(this.total || 0)
      var currentAdults = Number(this.bookingDetails.adults || 0)
      return Math.max(0, (totalCapacity - totalGuests) + currentAdults)
    },

    calculateChildMax() {
      if (!this.selectedFacility) return 10
      var maxGuests = Number(this.selectedFacility.maximum_guests || 0)
      var quantity = Number(this.selectedFacility.quantity || 1)
      var totalCapacity = maxGuests * quantity
      var totalGuests = Number(this.total || 0)
      var currentChildren = Number(this.bookingDetails.children || 0)
      return Math.max(0, (totalCapacity - totalGuests) + currentChildren)
    },

    total() {
      return Number(this.bookingDetails.adults || 0) + Number(this.bookingDetails.children || 0)
    }
  },

  methods: {
    loadDatesFromGlobalStore() {
      if (this.hasGlobalBookingDates) {
        this.facility.start_date = this.globalBookingDates.check_in
        this.facility.end_date = this.globalBookingDates.check_out
        return
      }
      try {
        const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
        if (userData.check_in && userData.check_out) {
          this.facility.start_date = userData.check_in
          this.facility.end_date = userData.check_out
        }
      } catch (e) {}
    },

    getStorageAdults() {
      try {
        const guests = JSON.parse(localStorage.getItem('guests') || '{}')
        if (guests.adults !== undefined && guests.adults !== null && guests.adults !== '') {
          return Number(guests.adults || 0)
        }
      } catch (e) {}

      try {
        const conservation = JSON.parse(localStorage.getItem('conservation_data') || '[]')
        if (Array.isArray(conservation) && conservation.length > 0) {
          return conservation.reduce(function (sum, fee) {
            const type = fee.payment_person ? fee.payment_person.toLowerCase() : ''
            if (type.includes('child') || type.includes('infant')) return sum
            return sum + Number(fee.payment_guests || 0)
          }, 0)
        }
      } catch (e) {}

      try {
        const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
        return Number(userData.adults || 0)
      } catch (e) { return 0 }
    },

    getStorageChildren() {
      try {
        const guests = JSON.parse(localStorage.getItem('guests') || '{}')
        if (guests.children !== undefined && guests.children !== null && guests.children !== '') {
          return Number(guests.children || 0)
        }
      } catch (e) {}

      try {
        const conservation = JSON.parse(localStorage.getItem('conservation_data') || '[]')
        if (Array.isArray(conservation) && conservation.length > 0) {
          return conservation.reduce(function (sum, fee) {
            const type = fee.payment_person ? fee.payment_person.toLowerCase() : ''
            if (!type.includes('child')) return sum
            if (type.includes('infant')) return sum
            return sum + Number(fee.payment_guests || 0)
          }, 0)
        }
      } catch (e) {}

      try {
        const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
        return Number(userData.children || 0)
      } catch (e) { return 0 }
    },

    loadGuestsFromStorage() {
      var adults = this.getStorageAdults()
      var children = this.getStorageChildren()

      if ((adults === 0 && children === 0) && this.hasGlobalGuests) {
        adults = this.globalGuests.adults
        children = this.globalGuests.children
      }

      if (adults > 0 || children > 0) {
        this.bookingDetails.adults = adults
        this.bookingDetails.children = children
      }
    },

    autoSearchIfDatesAvailable() {
      if (!this.facility.start_date || !this.facility.end_date) return
      const range = `${this.facility.start_date}|${this.facility.end_date}`
      if (this.lastSearchedRange === range) return
      this.searchFacilityAvailability()
    },

    onBookingDatesChanged(dates) {
      if (!dates || !dates.check_in || !dates.check_out) return
      const same = this.facility.start_date === dates.check_in &&
                   this.facility.end_date === dates.check_out
      if (same) return
      this.facility.start_date = dates.check_in
      this.facility.end_date = dates.check_out
      this.searchFacilityAvailability()
    },

    calculateExtraPax() {
      const standardCapacity = this.standardCapacity
      const totalGuests = this.totalStandardGuests

      if (totalGuests > standardCapacity && standardCapacity > 0) {
        this.showExtraPaxSection = true
        const extraCount = totalGuests - standardCapacity

        let extraAdults = 0
        let extraChildren = 0

        if (this.bookingDetails.adults > 0 && this.bookingDetails.children > 0) {
          const adultRatio = this.bookingDetails.adults / totalGuests
          extraAdults = Math.round(extraCount * adultRatio)
          extraChildren = extraCount - extraAdults

          if (extraChildren > this.bookingDetails.children) {
            extraChildren = this.bookingDetails.children
            extraAdults = extraCount - extraChildren
          }
          if (extraAdults > this.bookingDetails.adults) {
            extraAdults = this.bookingDetails.adults
            extraChildren = extraCount - extraAdults
          }
        } else if (this.bookingDetails.adults > 0) {
          extraAdults = Math.min(extraCount, this.bookingDetails.adults)
          extraChildren = 0
        } else if (this.bookingDetails.children > 0) {
          extraAdults = 0
          extraChildren = Math.min(extraCount, this.bookingDetails.children)
        }

        this.bookingDetails.extra_pax_adults = extraAdults
        this.bookingDetails.extra_pax_children = extraChildren
      } else {
        this.showExtraPaxSection = false
        this.bookingDetails.extra_pax_adults = 0
        this.bookingDetails.extra_pax_children = 0
      }
    },

    validateExtraPax() {
      if (this.bookingDetails.extra_pax_adults < 0) this.bookingDetails.extra_pax_adults = 0
      if (this.bookingDetails.extra_pax_children < 0) this.bookingDetails.extra_pax_children = 0
      if (isNaN(this.bookingDetails.extra_pax_adults)) this.bookingDetails.extra_pax_adults = 0
      if (isNaN(this.bookingDetails.extra_pax_children)) this.bookingDetails.extra_pax_children = 0
    },

    searchFacilityAvailability() {
      var self = this

      if (!this.facility.start_date || !this.facility.end_date) {
        return
      }

      const range = `${this.facility.start_date}|${this.facility.end_date}`
      this.lastSearchedRange = range

      var actionToUse = null
      if (this.$store._actions && this.$store._actions['check_date_facility_public_availability']) {
        actionToUse = 'check_date_facility_public_availability'
      } else if (this.$store._actions && this.$store._actions['check_date_facility_availability']) {
        actionToUse = 'check_date_facility_availability'
      }

      if (!actionToUse) {
        return
      }

      this.showFullPageLoader = true

      if (this.$store.commit) {
        this.$store.commit('REMOVE_AVAILABLE_FACILITIES')
      }

      this.$store.dispatch(actionToUse, {
        check_in_date: this.facility.start_date,
        check_out_date: this.facility.end_date
      })
        .then(function () {
          self.showFullPageLoader = false
        })
        .catch(function () {
          self.showFullPageLoader = false
        })
    },

    resetForAvailability() {
      this.facility.end_date = ''
      if (this.$store.commit) {
        this.$store.commit('REMOVE_AVAILABLE_FACILITIES')
      }
      this.selectedFacility = null
      this.showFullPageLoader = false
    },

    openInfo(accommodation) {
      this.currentInfoFacility = accommodation
      this.currentInfoImageIndex = 0
      if (this.$store.dispatch && this.$store._actions && this.$store._actions['get_facility_images']) {
        this.$store.dispatch('get_facility_images', accommodation.public_id)
      }
      this.infoDialog = true
    },

    prevInfoImage() {
      if (this.currentInfoImageIndex > 0) {
        this.currentInfoImageIndex--
      }
    },

    nextInfoImage() {
      if (this.currentInfoImageIndex < this.infoImages.length - 1) {
        this.currentInfoImageIndex++
      }
    },

    openBookingDialog(accommodation) {
      this.selectedFacility = accommodation

      var storedAdults = this.getStorageAdults()
      var storedChildren = this.getStorageChildren()

      this.bookingDetails = {
        start_date: this.facility.start_date,
        end_date: this.facility.end_date,
        children: storedChildren || this.globalGuests.children || 0,
        adults: storedAdults || this.globalGuests.adults || 0,
        catering_type: '',
        accommodation_discount: 0,
        extra_pax_adults: 0,
        extra_pax_children: 0,
        number_of_rooms: 0
      }

      this.calculateExtraPax()
      this.showDates = true
      this.dialog = true

      if (this.$store.dispatch) {
        if (this.$store._actions && this.$store._actions['get_facility']) {
          this.$store.dispatch('get_facility', accommodation.public_id)
        }

        if (this.$store._actions && this.$store._actions['get_facility_with_pricing']) {
          this.$store.dispatch('get_facility_with_pricing', {
            date: this.$moment(this.facility.start_date).format('YYYY-MM-DD'),
            public_id: accommodation.public_id
          })
            .then(() => {
              this.$forceUpdate()
            })
            .catch(() => {})
        }
      }

      this.$nextTick(() => {
        if (this.pricingWatcher) {
          this.pricingWatcher()
        }
        this.pricingWatcher = this.$watch('pricedFacility', (newVal) => {
          if (newVal && newVal.price && newVal.price.length > 0) {
            this.$forceUpdate()
          }
        }, { deep: true })
      })
    },

    closeBookingDialog() {
      this.dialog = false
      this.selectedFacility = null
      this.showDates = false
      this.showExtraPaxSection = false

      if (this.pricingWatcher) {
        this.pricingWatcher()
        this.pricingWatcher = null
      }
    },

    getDays() {
      if (!this.bookingDetails.start_date || !this.bookingDetails.end_date) return 1
      var start = this.$moment(this.bookingDetails.start_date)
      var end = this.$moment(this.bookingDetails.end_date)
      var days = end.diff(start, 'days')
      return days > 0 ? days : 1
    },

    getRoomRate() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      var roomRate = Number(priceData.mandatory_price || priceData.price || priceData.adult_price || 0)
      var converted = this.convertCurrency(priceData.currency_type, roomRate)
      return converted ? converted.amount : 0
    },

    getAdultPrice() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]

      if (this.pricedFacility.accomodation_type_id === 'Stables' && this.pricedFacility.maximum_guests <= 2) {
        return this.getRoomRate()
      }

      var adultPrice = Number(priceData.adult_price || priceData.price || 0)
      var converted = this.convertCurrency(priceData.currency_type, adultPrice)
      return converted ? converted.amount : 0
    },

    getChildPrice() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]

      if (this.pricedFacility.accomodation_type_id === 'Stables' && this.pricedFacility.maximum_guests <= 2) {
        return this.getRoomRate()
      }

      var childPrice = Number(priceData.child_price || priceData.price || 0)
      var converted = this.convertCurrency(priceData.currency_type, childPrice)
      return converted ? converted.amount : 0
    },

    getExtraAdultRate() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      var rate = Number(
        this.pricedFacility.cost_per_extra_adult ||
        priceData.cost_per_extra_adult ||
        priceData.adult_price ||
        priceData.mandatory_price ||
        priceData.price ||
        0
      )
      var converted = this.convertCurrency(priceData.currency_type, rate)
      return converted ? converted.amount : 0
    },

    getExtraChildRate() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      var rate = Number(
        this.pricedFacility.cost_per_extra_child ||
        priceData.cost_per_extra_child ||
        priceData.child_price ||
        0
      )
      if (rate === 0 && priceData.adult_price) {
        rate = Number(priceData.adult_price) * 0.5
      }
      var converted = this.convertCurrency(priceData.currency_type, rate)
      return converted ? converted.amount : 0
    },

    calculateAdultTotal() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0

      if (this.pricedFacility.accomodation_type_id === 'Stables' && this.pricedFacility.maximum_guests <= 2) {
        return 0
      }

      var priceData = this.pricedFacility.price[0]
      var adultPrice = Number(priceData.adult_price || 0)
      var adults = Number(this.bookingDetails.adults || 0)
      var total = adultPrice * adults * this.getDays()
      var converted = this.convertCurrency(priceData.currency_type, total)
      return converted ? converted.amount : 0
    },

    calculateChildTotal() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0

      if (this.pricedFacility.accomodation_type_id === 'Stables' && this.pricedFacility.maximum_guests <= 2) {
        return 0
      }

      var priceData = this.pricedFacility.price[0]
      var childPrice = Number(priceData.child_price || 0)
      var children = Number(this.bookingDetails.children || 0)
      var total = childPrice * children * this.getDays()
      var converted = this.convertCurrency(priceData.currency_type, total)
      return converted ? converted.amount : 0
    },

    calculateExtraAdultTotal() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0

      var priceData = this.pricedFacility.price[0]
      var extraAdults = Number(this.bookingDetails.extra_pax_adults || 0)
      var days = this.getDays()

      var rate = Number(
        this.pricedFacility.cost_per_extra_adult ||
        priceData.cost_per_extra_adult ||
        priceData.adult_price ||
        priceData.mandatory_price ||
        priceData.price ||
        0
      )

      var total = rate * extraAdults * days
      var converted = this.convertCurrency(priceData.currency_type, total)
      return converted ? converted.amount : 0
    },

    calculateExtraChildTotal() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0

      var priceData = this.pricedFacility.price[0]
      var extraChildren = Number(this.bookingDetails.extra_pax_children || 0)
      var days = this.getDays()

      var rate = Number(
        this.pricedFacility.cost_per_extra_child ||
        priceData.cost_per_extra_child ||
        priceData.child_price ||
        0
      )

      if (rate === 0 && priceData.adult_price) {
        rate = Number(priceData.adult_price) * 0.5
      }

      var total = rate * extraChildren * days
      var converted = this.convertCurrency(priceData.currency_type, total)
      return converted ? converted.amount : 0
    },

    getPelicanPackagePrice() {
      if (!this.pricedFacility || !this.pricedFacility.price) return 0
      if (!this.bookingDetails.catering_type) return 0

      var packagePrice = 0
      var currencyType = this.pricedFacility.price[0].currency_type

      if (this.bookingDetails.catering_type === '9a9a77eb') {
        for (var i = 0; i < this.pricedFacility.price.length; i++) {
          var item = this.pricedFacility.price[i]
          if (item.hasOwnProperty('lower_limit_list') && item.lower_limit_list.length > 0) {
            packagePrice = item.price
            currencyType = item.currency_type
            break
          }
        }
      } else if (this.bookingDetails.catering_type === '266677c3') {
        for (var j = 0; j < this.pricedFacility.price.length; j++) {
          var item2 = this.pricedFacility.price[j]
          if (item2.hasOwnProperty('upper_upper_limit_list') && item2.upper_upper_limit_list.length > 0) {
            packagePrice = item2.price
            currencyType = item2.currency_type
            break
          }
        }
      } else if (this.bookingDetails.catering_type === '377788d4') {
        for (var k = 0; k < this.pricedFacility.price.length; k++) {
          var item3 = this.pricedFacility.price[k]
          if (item3.hasOwnProperty('middle_middle_limit_list') && item3.middle_middle_limit_list.length > 0) {
            packagePrice = item3.price
            currencyType = item3.currency_type
            break
          }
        }
      }

      var converted = this.convertCurrency(currencyType, packagePrice)
      return converted ? converted.amount : 0
    },

    calculateTotalPrice() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0

      var priceData = this.pricedFacility.price[0]
      var days = this.getDays()
      var total = 0

      if (this.pricedFacility.accomodation_type_id === 'Stables') {
        if (this.pricedFacility.maximum_guests <= 2) {
          var roomRate = Number(priceData.mandatory_price || priceData.price || priceData.adult_price || 0)
          total = roomRate * days
        } else {
          var adultPrice = Number(priceData.adult_price || 0)
          var childPrice = Number(priceData.child_price || 0)
          var adults = Number(this.bookingDetails.adults || 0)
          var children = Number(this.bookingDetails.children || 0)
          total = (adultPrice * adults + childPrice * children) * days
        }
      } else if (this.pricedFacility.accomodation_type_id === 'Pelican' && this.bookingDetails.catering_type) {
        total = this.getPelicanPackagePrice() * days
      } else {
        var adultPrice2 = Number(priceData.adult_price || 0)
        var childPrice2 = Number(priceData.child_price || 0)
        var adults2 = Number(this.bookingDetails.adults || 0)
        var children2 = Number(this.bookingDetails.children || 0)
        total = (adultPrice2 * adults2 + childPrice2 * children2) * days
      }

      var convertedAmount = this.convertCurrency(priceData.currency_type, total)
      return convertedAmount ? convertedAmount.amount : 0
    },

    calculateTotalPriceWithExtras() {
      var baseTotal = this.calculateTotalPrice()
      var extraTotal = 0

      if (this.bookingDetails.extra_pax_adults > 0) {
        extraTotal += this.calculateExtraAdultTotal()
      }

      if (this.bookingDetails.extra_pax_children > 0) {
        extraTotal += this.calculateExtraChildTotal()
      }

      return this.$roundOff(baseTotal + extraTotal)
    },

    convertCurrency(currencyType, amount) {
      if (this.convert1) {
        return this.convert1(currencyType, amount)
      }
      return { amount: amount }
    },

    addAccommodation() {
      if (!this.selectedFacility) { alert('No facility selected'); return }
      if (!this.bookingDetails.start_date || !this.bookingDetails.end_date) {
        alert('Please select start and end dates'); return
      }
      if (this.bookingDetails.start_date === this.bookingDetails.end_date) {
        alert('Check-out date must be after check-in date'); return
      }
      if (this.selectedFacility.accomodation_type_id === 'Pelican' && !this.bookingDetails.catering_type) {
        alert('Please select a catering type for Pelican accommodation'); return
      }
      if (this.total === 0 &&
        Number(this.bookingDetails.extra_pax_adults || 0) === 0 &&
        Number(this.bookingDetails.extra_pax_children || 0) === 0) {
        alert('Please select at least one guest'); return
      }

      var facility = this.selectedFacility
      var priceData = facility.price && facility.price[0] ? facility.price[0] : null
      if (!priceData) { alert('No pricing available for the selected dates'); return }

      var adults = Number(this.bookingDetails.adults || 0)
      var children = Number(this.bookingDetails.children || 0)
      var extraAdults = Number(this.bookingDetails.extra_pax_adults || 0)
      var extraChildren = Number(this.bookingDetails.extra_pax_children || 0)

      var bookingData = {
        public_id: facility.public_id,
        catering_type: this.bookingDetails.catering_type,
        facility_type: facility.facility_type_id,
        accommodation_type: facility.accomodation_type_id,
        start_date: this.bookingDetails.start_date,
        end_date: this.bookingDetails.end_date,
        name: facility.name,
        children: children,
        adults: adults,
        extra_pax_adults: extraAdults,
        extra_pax_children: extraChildren,
        accommodation_discount: Number(this.bookingDetails.accommodation_discount || 0),
        number_of_rooms: Number(this.bookingDetails.number_of_rooms || 0)
      }

      if (facility.accomodation_type_id === 'Pelican') {
        let priceIndex
        let currencyList = []
        if (this.bookingDetails.catering_type === '9a9a77eb') {
          facility.price.map(function (item, index) {
            if (item.hasOwnProperty('lower_limit_list') && item.lower_limit_list.length > 0) {
              priceIndex = index
              currencyList = item.upper_currency
            }
          })
        } else if (this.bookingDetails.catering_type === '266677c3') {
          facility.price.map(function (item, index) {
            if (item.hasOwnProperty('upper_upper_limit_list') && item.upper_upper_limit_list.length > 0) {
              priceIndex = index
              currencyList = item.lower_currency
            }
          })
        } else if (this.bookingDetails.catering_type === '377788d4') {
          facility.price.map(function (item, index) {
            if (item.hasOwnProperty('middle_middle_limit_list') && item.middle_middle_limit_list.length > 0) {
              priceIndex = index
              currencyList = item.upper_currency
            }
          })
        }
        Object.assign(bookingData, {
          pack_prices: facility.price[priceIndex],
          pack_currency: facility.price[priceIndex],
          pelican_lower_currency: currencyList
        })
      }
      else if (facility.accomodation_type_id === 'Stables') {
        switch (Number(facility.maximum_guests)) {
          case 1: {
            var billableStandardAdults = adults - extraAdults
            if (billableStandardAdults < 0) billableStandardAdults = 0

            Object.assign(bookingData, {
              quantity: facility.quantity,
              maximum_guests: facility.maximum_guests,
              child_price: facility.price[0].child_price,
              mandatory_price: facility.price[0].mandatory_price,
              currency: facility.price[0].currency_type,
              adult_price: facility.price[0].mandatory_price * billableStandardAdults,
              cost_per_extra_adult: facility.price[0].mandatory_price,
              cost_per_extra_child: facility.price[0].child_price
            })
            break
          }
          case 2: {
            Object.assign(bookingData, {
              adult_price: facility.price[0].adult_price,
              child_price: facility.price[0].child_price,
              mandatory_price: facility.price[0].mandatory_price,
              currency: facility.price[0].currency_type,
              maximum_guests: facility.maximum_guests,
              cost_per_extra_adult: facility.price[0].mandatory_price,
              cost_per_extra_child: facility.price[0].child_price
            })
            break
          }
          default: {
            Object.assign(bookingData, {
              adult_price: facility.price[0].adult_price,
              child_price: facility.price[0].child_price,
              mandatory_price: facility.price[0].mandatory_price,
              currency: facility.price[0].currency_type,
              maximum_guests: facility.maximum_guests,
              cost_per_extra_adult: facility.price[0].mandatory_price,
              cost_per_extra_child: facility.price[0].child_price
            })
          }
        }
      }
      else {
        Object.assign(bookingData, {
          adult_price: facility.price[0].adult_price,
          child_price: facility.price[0].child_price,
          mandatory_price: facility.price[0].mandatory_price,
          currency: facility.price[0].currency_type,
          cost_per_extra_adult: facility.price[0].mandatory_price,
          cost_per_extra_child: facility.price[0].child_price
        })
      }

      var existingData = JSON.parse(localStorage.getItem('facility_data') || '[]')
      existingData.push(bookingData)
      localStorage.setItem('facility_data', JSON.stringify(existingData))

      this.$root.$emit('cart-updated')

      if (this.$store.commit) {
        this.$store.commit('ADD_FACILITY', bookingData)
      }

      alert(facility.name + ' has been added to your booking')
      this.closeBookingDialog()
    },

    developPricePacks(accommodation) {
      var prices = []
      if (accommodation.accomodation_type_id !== 'Stables' && accommodation.price) {
        for (var p = 0; p < accommodation.price.length; p++) {
          var price = accommodation.price[p]
          if (price.lower_limit_list) {
            for (var l = 0; l < price.lower_limit_list.length; l++) {
              prices.push({
                lower_limit: price.lower_limit_list[l],
                upper_limit: price.upper_limit_list[l],
                price: price.price,
                currency: price.currency_type
              })
            }
          }
        }
      }
      return prices
    }
  },

  watch: {
    globalBookingDates: {
      handler: function (newDates) {
        if (!newDates || !newDates.check_in || !newDates.check_out) return

        var same = this.facility.start_date === newDates.check_in &&
          this.facility.end_date === newDates.check_out
        if (same) return

        this.facility.start_date = newDates.check_in
        this.facility.end_date = newDates.check_out
        this.searchFacilityAvailability()
      },
      deep: true
    },

    globalGuests: {
      handler: function (newGuests) {
        if (!newGuests) return
        if (this.bookingDetails.adults === 0 && this.bookingDetails.children === 0 &&
            (newGuests.adults > 0 || newGuests.children > 0)) {
          this.bookingDetails.adults = newGuests.adults
          this.bookingDetails.children = newGuests.children
          this.calculateExtraPax()
        }
      },
      deep: true
    },

    'bookingDetails.adults': {
      handler: function () {
        this.calculateExtraPax()
      }
    },

    'bookingDetails.children': {
      handler: function () {
        this.calculateExtraPax()
      }
    }
  }
}
</script>

<style scoped>
@font-face {
  font-family: 'Earthwerk';
  src: url('PizzaDude.dk-Earthwerk.otf');
  font-weight: normal;
  font-style: normal;
}

.accommodation-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  position: relative;
}

.date-selection-card {
  border-radius: 12px;
  overflow: hidden;
}

.date-selection-header {
  background-color: #f5f5f5;
  border-bottom: 1px solid #eee;
}

.date-selection-header h3 {
  margin: 0;
  color: #74a533;
}

.earth-color {
  color: #74a533;
  margin-left: 5%;
}

.slots-explanation {
  color: #666;
  font-size: 12px;
  margin-left: 5%;
  display: block;
}

.accommodations-grid-container {
  transition: opacity 0.3s ease;
}

.accommodations-grid-container.loading-active {
  opacity: 0.3;
  pointer-events: none;
}

.accommodation-card {
  border-radius: 8px !important;
  transition: all 0.3s ease;
  overflow: hidden;
}

.accommodation-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
}

.accommodation-image {
  border-radius: 8px 8px 0 0 !important;
  transition: transform 0.5s ease;
  overflow: hidden;
}

.accommodation-card:hover .accommodation-image {
  transform: scale(1.05);
}

.image-overlay {
  padding: 16px;
}

.accommodation-badge {
  background-color: rgba(116, 165, 51, 0.9);
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  display: inline-block;
}

.earthwerk-font {
  font-family: 'Earthwerk', sans-serif;
  color: #333;
  font-size: 20px;
  line-height: 1.3;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
}

.accommodation-title {
  transition: color 0.2s ease;
}

.accommodation-title:hover {
  color: #74a533 !important;
}

.capacity-display {
  display: flex;
  gap: 20px;
  margin-top: 15px;
  flex-wrap: wrap;
}

.capacity-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.capacity-label {
  font-size: 14px;
  color: #666;
}

.capacity-value {
  font-size: 16px;
  font-weight: 600;
  color: #74a533;
}

.info-icon {
  cursor: pointer;
  color: #666;
  margin-left: 10px;
  vertical-align: middle;
}

.info-icon:hover {
  color: #74a533;
}

.price-info {
  color: #666;
  font-size: 14px;
}

.price-pack {
  margin-bottom: 6px;
}

.price-pack span:last-child {
  color: #e67e22;
  font-weight: 600;
}

.accommodation-details {
  border-top: 1px solid #eee;
  padding-top: 12px;
}

.book-now-btn {
  width: 100%;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.full-page-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  backdrop-filter: blur(4px);
  border-radius: 12px;
}

.loader-content {
  text-align: center;
}

.loader-text {
  font-size: 16px;
  color: #74a533;
  font-weight: 500;
}

.info-dialog,
.booking-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

.gallery-section {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
}

.gallery-container,
.single-image-container {
  width: 100%;
  height: 400px;
}

.image-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  position: relative;
}

.image-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.7);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
}

.nav-button:hover {
  background: rgba(255, 255, 255, 0.9);
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.nav-button.prev {
  left: 20px;
}

.nav-button.next {
  right: 20px;
}

.image-counter {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 14px;
  z-index: 10;
}

.details-section {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.details-header {
  margin-bottom: 25px;
  border-bottom: 1px solid #eee;
  padding-bottom: 20px;
}

.facility-title {
  font-family: 'Earthwerk', sans-serif;
  color: #333;
  font-size: 32px;
  margin-bottom: 15px;
  letter-spacing: 0.5px;
}

.description-section {
  color: #555;
  line-height: 1.8;
  font-size: 16px;
  margin-bottom: 30px;
}

.dialog-header {
  background-color: #74a533;
  color: white;
  padding: 20px 24px;
}

.dialog-header h2 {
  font-family: 'Earthwerk', sans-serif;
  font-size: 24px;
  margin: 0;
}

.dialog-content {
  padding: 24px;
}

.tab-content {
  padding: 15px 0;
}

.date-pickers {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 25px;
}

.date-input {
  margin-bottom: 15px;
}

.participant-selectors {
  margin: 20px 0;
}

.selector-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.participant-input {
  margin-bottom: 15px;
}

.global-guests-info {
  margin: 15px 0;
}

.extra-pax-section {
  margin-top: 15px;
  padding: 10px;
  background-color: #fff3e0;
  border-radius: 8px;
  border-left: 4px solid #ff9800;
}

.additional-options {
  margin-top: 15px;
}

.option-input {
  margin-bottom: 15px;
}

.price-summary {
  margin-top: 25px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.price-breakdown {
  margin-top: 10px;
}

.price-line {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.price-line.extra-charge {
  color: #e67e22;
  font-weight: 500;
}

.price-total {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 2px solid #74a533;
  font-size: 16px;
  font-weight: bold;
}

.dialog-actions {
  padding: 16px 24px;
  border-top: 1px solid #eee;
}

.cancel-btn {
  margin-right: 10px;
}

.reserve-btn {
  font-weight: 600;
  letter-spacing: 0.5px;
}

@media (max-width: 960px) {
  .earthwerk-font {
    font-size: 18px;
  }

  .date-pickers,
  .selector-group {
    grid-template-columns: 1fr;
  }

  .capacity-display {
    flex-direction: column;
    gap: 10px;
  }

  .gallery-container,
  .single-image-container {
    height: 300px;
  }

  .facility-title {
    font-size: 28px;
  }
}

@media (max-width: 600px) {
  .accommodation-card {
    margin-bottom: 16px;
  }

  .earthwerk-font {
    font-size: 16px;
  }

  .dialog-header h2 {
    font-size: 20px;
  }

  .gallery-container,
  .single-image-container {
    height: 250px;
  }

  .facility-title {
    font-size: 24px;
  }

  .nav-button {
    width: 35px;
    height: 35px;
  }

  .nav-button.prev {
    left: 10px;
  }

  .nav-button.next {
    right: 10px;
  }

  .image-counter {
    font-size: 12px;
    padding: 3px 10px;
  }
}
</style>